<template>
  <WebccLink
    :to="targetUrl"
    class="cursor-pointer px-1 pb-6"
    @click.stop="onViewRecommendation"
  >
    <article class="relative flex flex-col rounded-lg bg-bgr shadow-md h-full">
      <figure
        class="relative flex min-h-[16rem] flex-row justify-center overflow-hidden rounded-lg"
      >
        <NuxtImg
          loading="lazy"
          class="w-full rounded-lg object-cover [transition:0.5s_all_ease-in-out] hover:scale-105 bg-no-repeat bg-center bg-[length:80%]"
          :class="`bg-[url('/logos/interhome.svg')]`"
          :src="
            config.public.imagekitBase +
            '/' +
            accom.image +
            '/' +
            transformation
          "
          :alt="accom.accom"
        />
      </figure>

      <client-only v-if="useStorageReady()">
        <button
          class="absolute right-2 top-2 rounded-full bg-bgr-200 p-1.5"
          @click.stop.prevent="toggleBookmark"
        >
          <WebccIcon
            filled
            :name="isBookmarked ? 'site/bookmark-filled' : 'site/bookmark'"
            class="h-4 w-4 stroke-2 text-txt"
          />
        </button>
      </client-only>
      <HitboxTags
        class="absolute left-2 top-2"
        :tags="accom.tags || []"
        :discount="discount"
      />

      <main class="flex flex-col items-start p-2 h-full">
        <section class="grow w-full">
          <div
            class="flex flex-row min-h-[2rem] items-center justify-between gap-2"
          >
            <div v-if="accom.evaluation?.stars" class="flex grow-0 flex-row">
              <WebccIcon
                v-for="s in accom.evaluation?.stars"
                :key="s"
                name="content/evaluation-star"
                filled
                class="h-4 w-4"
              />
            </div>
            <div
              class="flex min-w-0 grow basis-0 flex-row justify-start gap-1 whitespace-nowrap font-semibold"
            >
              <span class="grow-0">{{
                accom.type?.translations[confData.language]
              }}</span>
              <span
                class="min-w-0 grow basis-0 overflow-x-hidden text-ellipsis"
                >{{ accom.slug ? accom.label : accom.accom }}</span
              >
            </div>
            <div class="grow-0">
              <WebccIcon
                v-if="accom.evaluation?.sustainable > 0"
                name="detailpage/hearttree"
                filled
                class="h-6 w-6"
              />
            </div>
          </div>
        </section>

        <div
          v-if="rating.value.value"
          class="mt-3 flex flex-row items-center gap-2 text-xs"
        >
          <span
            class="whitespace-nowrap rounded-md rounded-bl-none bg-thm px-2 py-1 text-txt-contrast font-bold"
          >
            {{ rating.text.value }}
          </span>
          <span>{{
            $t(`${trPrefix}Rating.review_s`, accom.rating?.numReviews)
          }}</span>
        </div>

        <span class="text-base text-txt-weak">{{
          [
            accom.place?.translations[confData.language].name,
            accom.country?.translations[confData.language].name,
          ].join(', ')
        }}</span>

        <div
          v-if="accom.minPrice?.price"
          class="flex flex-row items-center gap-1 text-sm font-medium sm:text-base"
        >
          <span>{{ $t(`${trPrefix}Price.from`) }}</span>
          <span>
            {{ toMoney(accom.minPrice.price) }}
            /
            <span class="text-base">{{
              $t(`${trPrefix}Price.night_s`, accom.minPrice.duration)
            }}</span>
          </span>
          <span
            v-if="accom.minPrice?.reduction && accom.minPrice?.regular"
            class="text-sm font-normal text-hlt line-through"
            >{{ toMoney(accom.minPrice.regular) }}</span
          >
          <span v-tooltip="tooltip" class="cursor-help" @click.prevent.stop>
            <WebccIcon
              filled
              name="site/info-new"
              class="-mt-0.5 inline-block h-4 w-4 text-txt-weak"
            />
          </span>
        </div>
      </main>
    </article>
  </WebccLink>
</template>

<script setup lang="ts">
const transformations = {
  xl: 'tr:w-640,h-360',
  lg: 'tr:w-640,h-360',
  md: 'tr:w-640,h-360',
  sm: 'tr:w-640,h-360',
  xs: 'tr:w-640,h-360',
}
const trPrefix = 'www.components.hitbox.'

const props = withDefaults(
  defineProps<{
    accom: PreparedItem
    currency: string
    languagePrefix: string
    tracker?: 'recommendation' | 'handpicked'
  }>(),
  {
    tracker: 'recommendation',
  },
)

const { t } = useI18n()
const { toMoney } = useMoneyFormat(() => props.currency)
const { buildAccommodationUrl } = useURLs()
const confData = useConfdata()
const bookmarks = useBookmarks()
const config = useRuntimeConfig()
const screen = useScreen()
const tracking = useTracking()
const rating = useRating(() => props.accom.rating.overall)
const partner = usePartnerStore()
const addToWishlistEvt = useEventBus<AccommodationDto>('addToWishlist')
const deleteFromWishlistEvt =
  useEventBus<AccommodationDto>('deleteFromWishlist')

const transformation = computed(
  () => transformations[screen.current] || 'tr:w-640,h-360',
)

const targetUrl = computed(() =>
  buildAccommodationUrl({ slug: props.accom.slug, code: props.accom.accom }),
)

const tooltip = computed(() => ({
  content: [
    '<div class="max-w-[12rem] p-1">',
    `  <strong>${t(`${trPrefix}Price.dependentPrice`)}</strong>`,
    `  <p class='mb-2'>${t(`${trPrefix}Price.priceAfterInput`)}</p>`,
    `  <strong>${t(`${trPrefix}Price.addCosts`)}</strong>`,
    `  <p class='mb-2'>${t(`${trPrefix}Price.addCostsLong`)}</p>`,
    '</div>',
  ].join(''),
  html: true,
  triggers: ['hover', 'click'],
}))

const isBookmarked = computed(() => bookmarks.has(props.accom.accom))

const discount = computed(() => {
  const { price, reduction } = props.accom.minPrice
  const origPrice = price + reduction

  return origPrice ? (reduction / origPrice) * 100 : 0
})

function onViewRecommendation() {
  tracking.handlers?.[props.tracker].view([props.accom])
}

function toggleBookmark() {
  if (partner.id && partner.jwt) {
    processAsPartner()
  } else {
    addToWishlist()
  }
}

function processAsPartner() {
  bookmarks.toggle(props.accom.accom)
  tracking.handlers?.[props.tracker].toggleWishlist(
    [props.accom],
    isBookmarked.value,
  )
}

function addToWishlist() {
  if (isBookmarked.value) {
    deleteFromWishlistEvt.emit({ code: props.accom.accom })
  } else {
    addToWishlistEvt.emit({ code: props.accom.accom })
  }
}
</script>
